import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Avatar,
  AvatarGroup,
} from '@mui/material';
import { format } from 'date-fns';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useEventOperationsData, useCommonEventData } from '../../contexts/EventContext';
import axios from 'axios';
import { getBackendUserUrl } from '../../utils/utils';
import AddEditTaskDialog from './AddEditTaskDialog';
import TaskStatusChip from './TaskStatusChip';
import PriorityChip from './PriorityChip';
import { TASK_STATUS, TASK_PRIORITY } from './taskModels';

const UserAvatars = ({ users }) => {
  if (!users?.length) {
    return (
      <Typography variant="caption" sx={{ 
        color: '#94a3b8',
        fontStyle: 'italic',
        textAlign: 'center'
      }}>
        Unassigned
      </Typography>
    );
  }
  
  return (
    <AvatarGroup 
      max={3} 
      sx={{ 
        '& .MuiAvatar-root': { 
          width: 32, 
          height: 32,
          fontSize: '0.875rem',
          fontWeight: 600,
          border: '2px solid #fff',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        },
        '& .MuiAvatarGroup-avatar': {
          backgroundColor: '#64748b',
          fontSize: '0.75rem'
        },
        justifyContent: 'center'
      }}
    >
      {users.map(user => (
        <Avatar
          key={user.userId}
          src={user.avatar}
          alt={`${user.firstName} ${user.lastName}`}
          sx={{ 
            width: 32, 
            height: 32,
            backgroundColor: '#3b82f6',
            '&:hover': {
              transform: 'scale(1.1)',
              transition: 'transform 0.2s ease-in-out',
              zIndex: 10
            }
          }}
          title={`${user.firstName} ${user.lastName}`}
        >
          {(user.firstName || user.lastName) ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}` : '?'}
        </Avatar>
      ))}
    </AvatarGroup>
  );
};

const TasksView = () => {
  const { 
    tasks = [], 
    agendas = [], 
    addTask, 
    updateTask, 
    deleteTask, 
    assignUsersToTask, 
    unassignUserFromTask,
    fetchTasks,
    isLoading
  } = useEventOperationsData();
  const { eventId, hostId } = useCommonEventData();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskStatus, setTaskStatus] = useState(TASK_STATUS.NEW);
  const [taskPriority, setTaskPriority] = useState(TASK_PRIORITY.MEDIUM);
  const [taskAgendaId, setTaskAgendaId] = useState('');
  const [taskStartTime, setTaskStartTime] = useState('');
  const [taskEndTime, setTaskEndTime] = useState('');
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [eventParticipants, setEventParticipants] = useState([]);

  // Fetch event participants
  useEffect(() => {
    const fetchParticipants = async () => {
      try {
        const response = await axios.get(`${getBackendUserUrl()}/event/event_participants/${eventId}`);
        // Filter out current user and sort alphabetically
        const participants = response.data
          .filter(user => user && user.userId !== hostId)
          .sort((a, b) => {
            const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
            const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
            return nameA.localeCompare(nameB);
          });
        setEventParticipants(participants);
      } catch (error) {
        console.error('Error fetching event participants:', error);
      }
    };

    if (eventId && hostId) {
      fetchParticipants();
    }
  }, [eventId, hostId]);

  // Early return after all hooks
  if (!eventId || !hostId) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          {!eventId ? 'Event ID is missing' : 'Host ID is missing'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please refresh the page or contact support if this issue persists.
        </Typography>
      </Box>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2
      }}>
        <Typography variant="h6" sx={{ color: '#64748b' }}>
          Loading tasks...
        </Typography>
      </Box>
    );
  }

  // Ensure we have arrays even if context values are undefined
  const safeTasks = Array.isArray(tasks) ? tasks : [];
  const safeAgendas = Array.isArray(agendas) ? agendas : [];

  // Instead, display all tasks in a single list
  const allTasks = safeTasks;

  const handleAddTask = () => {
    setSelectedTask(null);
    setTaskTitle('');
    setTaskDescription('');
    setTaskStatus(TASK_STATUS.NEW);
    setTaskPriority(TASK_PRIORITY.MEDIUM);
    setTaskAgendaId('');
    setTaskStartTime('');
    setTaskEndTime('');
    setSelectedAssignees([]);
    setEditDialogOpen(true);
  };

  const handleEditClick = (task) => {
    setSelectedTask(task);
    setTaskTitle(task.name);
    setTaskDescription(task.description);
    setTaskStatus(task.status);
    setTaskPriority(task.priority);
    setTaskAgendaId(task.agendaId || '');
    setTaskStartTime(task.startTime ? format(new Date(task.startTime), 'HH:mm') : '');
    setTaskEndTime(task.endTime ? format(new Date(task.endTime), 'HH:mm') : '');
    setSelectedAssignees(task.TaskAssignees?.map(assignee => assignee.User) || []);
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    // Ensure we have the required data
    if (!hostId) {
      console.error('Cannot save task: hostId is null');
      alert('Host ID is missing. Please refresh the page and try again.');
      return;
    }

    if (!eventId) {
      console.error('Cannot save task: eventId is null');
      alert('Event ID is missing. Please refresh the page and try again.');
      return;
    }

    try {
      if (selectedTask) {
        // EDITING EXISTING TASK
        
        // 1. Update task fields if they have changed
        const taskFieldsChanged = 
          taskTitle !== selectedTask.name ||
          taskDescription !== selectedTask.description ||
          taskStatus !== selectedTask.status ||
          taskPriority !== selectedTask.priority ||
          taskAgendaId !== selectedTask.agendaId;
          
        if (taskFieldsChanged) {
          const updateData = {
            name: taskTitle,
            description: taskDescription,
            status: taskStatus,
            priority: taskPriority,
            agenda_id: taskAgendaId || null
          };
          
          await updateTask(selectedTask.taskId, updateData);
        }
        
        // 2. Handle assignee changes
        const currentAssigneeIds = (selectedTask.TaskAssignees || []).map(assignee => assignee.User.userId);
        const newAssigneeIds = selectedAssignees.map(user => user.userId);
        
        // Find users to add (in new list but not in current)
        const usersToAdd = newAssigneeIds.filter(id => !currentAssigneeIds.includes(id));
        
        // Find users to remove (in current list but not in new)
        const usersToRemove = currentAssigneeIds.filter(id => !newAssigneeIds.includes(id));
        
        // Add new assignees
        if (usersToAdd.length > 0) {
          await assignUsersToTask(selectedTask.taskId, usersToAdd);
        }
        
        // Remove assignees
        for (const userId of usersToRemove) {
          await unassignUserFromTask(selectedTask.taskId, userId);
        }
        
      } else {
        // CREATING NEW TASK
        
        // 1. Create the task first
        const taskData = {
          agenda_id: taskAgendaId || null,  // Ensure we explicitly pass null if no agenda is selected
          name: taskTitle,
          description: taskDescription,
          status: taskStatus,
          priority: taskPriority,
          created_by: hostId
        };

        const newTask = await addTask(taskData);

        // 2. Assign users to the new task if any are selected
        if (selectedAssignees.length > 0) {
          const userIds = selectedAssignees.map(user => user.userId);
          await assignUsersToTask(newTask.taskId, userIds);
        }
      }
      
      setEditDialogOpen(false);
      
    } catch (error) {
      console.error('Failed to save task:', error);
      console.error('Error details:', error.response?.data);
      // TODO: Show error message to user
    }
  };

  const TaskGroup = ({ title, tasks, showAgenda = false }) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2, color: '#1a1a1a', fontWeight: 600 }}>
        {title}
      </Typography>
      
      {/* Table Header */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: '100px 1fr 130px 120px 180px 180px 120px',
        gap: 2,
        mb: 1,
        px: 3,
        py: 2,
        bgcolor: '#f8fafc',
        borderRadius: 2,
        border: '1px solid #e2e8f0'
      }}>
        <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 600, alignSelf: 'center' }}>
          ID
        </Typography>
        <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 600, alignSelf: 'center' }}>
          Task
        </Typography>
        <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 600, alignSelf: 'center' }}>
          Status
        </Typography>
        <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 600, alignSelf: 'center' }}>
          Priority
        </Typography>
        <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 600, alignSelf: 'center' }}>
          Assignees
        </Typography>
        <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 600, alignSelf: 'center' }}>
          Agenda
        </Typography>
        <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 600, alignSelf: 'center' }}>
          Actions
        </Typography>
      </Box>

      {/* Table Rows */}
      {tasks.map((task, index) => (
        <Box
          key={task.taskId}
          sx={{
            display: 'grid',
            gridTemplateColumns: '100px 1fr 130px 120px 180px 180px 120px',
            gap: 2,
            alignItems: 'start',
            px: 3,
            py: 3,
            minHeight: 80,
            borderBottom: index === tasks.length - 1 ? 'none' : '1px solid #e2e8f0',
            backgroundColor: '#fff',
            '&:hover': {
              bgcolor: '#f8fafc',
            },
            '&:first-of-type': {
              borderTop: '1px solid #e2e8f0',
            },
            borderLeft: '1px solid #e2e8f0',
            borderRight: '1px solid #e2e8f0',
            ...(index === tasks.length - 1 && {
              borderBottom: '1px solid #e2e8f0',
              borderBottomLeftRadius: 2,
              borderBottomRightRadius: 2,
            })
          }}
        >
          {/* ID Column */}
          <Box sx={{ alignSelf: 'center' }}>
            <Typography variant="body2" sx={{ 
              color: '#64748b', 
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              fontWeight: 500
            }}>
              {task.taskId.slice(0, 8)}
            </Typography>
          </Box>

          {/* Task Column (Name + Description + Agenda) */}
          <Box sx={{ minHeight: 60, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 600, 
              color: '#1e293b',
              lineHeight: 1.3,
              mb: 0.5
            }}>
              {task.name}
            </Typography>
            {task.description && (
              <Typography variant="body2" sx={{ 
                color: '#64748b', 
                lineHeight: 1.4,
                mb: 0.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}>
                {task.description}
              </Typography>
            )}
          </Box>

          {/* Status Column */}
          <Box sx={{ alignSelf: 'center' }}>
            <TaskStatusChip status={task.status} />
          </Box>

          {/* Priority Column */}
          <Box sx={{ alignSelf: 'center' }}>
            <PriorityChip priority={task.priority} />
          </Box>

          {/* Assignees Column */}
          <Box sx={{ 
            alignSelf: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}>
            <UserAvatars users={task.TaskAssignees?.map(assignee => assignee.User) || []} />
            {task.TaskAssignees?.length > 0 && (
              <Typography variant="caption" sx={{ 
                color: '#64748b',
                fontSize: '0.75rem',
                textAlign: 'center'
              }}>
                {task.TaskAssignees.length} assignee{task.TaskAssignees.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>

          {/* Agenda Column */}
          <Box sx={{ alignSelf: 'center' }}>
            <Typography variant="body2" sx={{ 
              color: '#64748b',
              fontFamily: 'monospace',
              fontWeight: 500
            }}>
              {task.agendaId
                ? (safeAgendas.find(a => a.agendaId === task.agendaId)?.name || 'Unknown')
                : 'Unassigned'}
            </Typography>
          </Box>

          {/* Actions Column */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1,
            alignSelf: 'center'
          }}>
            <IconButton
              size="small"
              onClick={() => handleEditClick(task)}
              sx={{ 
                color: '#64748b',
                '&:hover': {
                  bgcolor: '#e2e8f0',
                  color: '#475569'
                }
              }}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => deleteTask(task.taskId, hostId)}
              sx={{ 
                color: '#64748b',
                '&:hover': {
                  bgcolor: '#fee2e2',
                  color: '#dc2626'
                }
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 4
      }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
          Tasks
        </Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddTask}
          variant="outlined"
          sx={{
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
            width: 'fit-content',
            px: 2,
            py: 1
          }}
        >
          Add Task
        </Button>
      </Box>

      {/* Tasks List */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <TaskGroup title="All Tasks" tasks={allTasks} showAgenda={true} />
      </Box>

      {/* Add/Edit Dialog */}
      <AddEditTaskDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSave={handleSave}
        selectedTask={selectedTask}
        taskTitle={taskTitle}
        setTaskTitle={setTaskTitle}
        taskDescription={taskDescription}
        setTaskDescription={setTaskDescription}
        taskStatus={taskStatus}
        setTaskStatus={setTaskStatus}
        taskPriority={taskPriority}
        setTaskPriority={setTaskPriority}
        taskAgendaId={taskAgendaId}
        setTaskAgendaId={setTaskAgendaId}
        taskStartTime={taskStartTime}
        setTaskStartTime={setTaskStartTime}
        taskEndTime={taskEndTime}
        setTaskEndTime={setTaskEndTime}
        selectedAssignees={selectedAssignees}
        setSelectedAssignees={setSelectedAssignees}
        eventParticipants={eventParticipants}
        agendas={safeAgendas}
      />
    </Box>
  );
};

export default TasksView;